<template>
	<main class="dashboard company-info">
		<MainLayout :chartLoaded="true" @change-section="onSectionChange" />
		<div class="main-section underHeader">
			<div class="ci-shell">
				<!-- Encabezado: el analista abre la ficha de un cliente puntual; el
				eyebrow codifica el tipo de empresa, el título es la razón social. -->
				<header class="ci-head">
					<div class="ci-head__intro">
						<p class="ci-eyebrow">
							Empresa {{ basicData.is_consolidated ? "acumulada (solo lectura)" : (basicData.company_type || "—") }}
						</p>
						<h1 class="ci-title">{{ basicData.company_name || "Cargando…" }}</h1>
						<p class="ci-rut">{{ companyRut }}</p>
					</div>
					<button type="button" class="pill" @click="back">
						<span class="pill__arrow" aria-hidden="true">←</span>
						Volver a cartera
					</button>
				</header>

				<div class="ci-body">
					<!-- Facetas de la ficha como navegación segmentada vertical -->
					<nav class="ci-rail" aria-label="Secciones de la empresa">
						<button
							v-for="tab in tabs"
							:key="tab.id"
							type="button"
							:class="['ci-tab', { 'is-active': activeTab === tab.id }]"
							@click="showContent(tab.id)"
						>
							{{ tab.label }}
						</button>
					</nav>

					<section class="ci-panel">
						<CompanyInfoBasicData
							v-if="activeTab === 'basic'"
							:basicData="basicData"
						/>
						<CompanyInfoBusinessType
							v-else-if="activeTab === 'businessType'"
							:basicData="basicData"
						/>
						<CompanyInfoAccountPlan
							v-else-if="activeTab === 'accountPlan'"
							:basicData="basicData"
						/>
						<CompanyInfoCategories
							v-else-if="activeTab === 'categories'"
							:company_id="basicData.companyId"
						/>
						<!-- Las subidas necesitan el companyId ya cargado: sus created()
						consultan la API con él (cutoff, última importación). -->
						<CompanyInfoUploadLedger
							v-else-if="activeTab === 'uploadLedger' && basicData.companyId"
							:idCompany="basicData.companyId"
						/>
						<CompanyInfoUploadSalesCosts
							v-else-if="activeTab === 'uploadSalesCosts' && basicData.companyId"
							:idCompany="basicData.companyId"
						/>
					</section>
				</div>
			</div>
		</div>
	</main>
</template>

<script>
import { mapActions } from "vuex";
import { consoleHandlerError } from "@/utils/message";
import MainLayout from "@/components/layout/MainLayout";
import CompanyInfoAccountPlan from "@/views/CompanyInfo_AccountPlan";
import CompanyInfoBasicData from "@/views/CompanyInfo_BasicData";
import CompanyInfoBusinessType from "@/views/CompanyInfo_BusinessType";
import CompanyInfoCategories from "@/views/CompanyInfo_Categories";
import CompanyInfoUploadLedger from "@/views/CompanyInfo_UploadLedger";
import CompanyInfoUploadSalesCosts from "@/views/CompanyInfo_UploadSalesCosts";

export default {
	name: "CompanyInfo",
	components: {
		MainLayout,
		CompanyInfoBasicData,
		CompanyInfoBusinessType,
		CompanyInfoCategories,
		CompanyInfoAccountPlan,
		CompanyInfoUploadLedger,
		CompanyInfoUploadSalesCosts,
	},
	data() {
		return {
			activeTab: "basic",
			basicData: {},
			companyRut: "",
		};
	},
	computed: {
		// Una empresa acumulada es de solo lectura y no tiene configuración
		// propia (tipo, plan, categorías, subidas): su ficha muestra solo los
		// datos básicos. Los miembros se gestionan desde el panel (modal
		// "Acumulada · miembros").
		tabs() {
			const all = [
				{ id: "basic", label: "Datos básicos" },
				{ id: "businessType", label: "Tipo de negocio" },
				{ id: "accountPlan", label: "Plan de cuentas" },
				{ id: "categories", label: "Categorías" },
				{ id: "uploadLedger", label: "Subir mayor" },
				{ id: "uploadSalesCosts", label: "Subir ventas/costos" },
			];
			if (this.basicData.is_consolidated) {
				return all.filter((tab) => tab.id === "basic");
			}
			return all;
		},
	},
	created() {
		this.loadFromRoute();
	},
	watch: {
		// Navegar de una ficha a otra (companyData → companyData con otro
		// companyId) REUTILIZA el componente: created() no vuelve a correr y
		// la ficha quedaba mostrando la empresa anterior.
		"$route.query.companyId"() {
			this.loadFromRoute();
		},
	},
	methods: {
		...mapActions("analystCompany", ["fetchCompanyDetail"]),
		loadFromRoute() {
			const companyId = this.$route.query.companyId;
			this.companyRut = this.$route.query.companyRut;
			if (!companyId) {
				this.$router.push({ name: "panel-analista" });
			} else {
				this.fetchCompanyData(companyId);
			}
		},
		async fetchCompanyData(id) {
			try {
				this.basicData = await this.fetchCompanyDetail(id);
				this.basicData.companyId = parseInt(id);
				// Al navegar de una empresa normal a una acumulada, el tab
				// activo puede haber desaparecido del rail.
				if (
					this.basicData.is_consolidated &&
					this.activeTab !== "basic"
				) {
					this.activeTab = "basic";
				}
			} catch (error) {
				consoleHandlerError({ error, silent: true });
			}
		},
		back() {
			this.$router.push({ name: "panel-analista" });
		},
		showContent(contentId) {
			this.activeTab = contentId;
		},
		onSectionChange(section, tab) {
			// CompanyInfo no es una sección del sidebar — cualquier click en el
			// rail navega al Dashboard con el query correspondiente (mismo
			// comportamiento que PanelAnalista.onSectionChange).
			const query = { section };
			if (tab) query.tab = tab;
			this.$router.push({ path: "/dashboard", query }).catch((err) => {
				if (err.name !== "NavigationDuplicated") throw err;
			});
		},
	},
};
</script>

<style scoped lang="scss">
// ============================================================================
// Ficha de empresa (vista del analista). Acotado a esta vista; consume el tema
// compartido del analista (var(--ap-*), elements/_analyst_theme.scss) para que
// la ficha, el panel de cartera y sus modales usen la MISMA identidad.
// ============================================================================
.company-info {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
}

.ci-shell {
	max-width: 1240px;
	margin: 0 auto;
	padding: 32px 28px 56px;
}

// Encabezado ----------------------------------------------------------------
.ci-head {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;
	flex-wrap: wrap;
	margin-bottom: 24px;
}

.ci-eyebrow {
	margin: 0 0 6px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--ap-teal);
}

.ci-title {
	margin: 0;
	font-weight: 700;
	font-size: 30px;
	line-height: 1.05;
	letter-spacing: -0.02em;
	color: var(--ap-ink);
}

.ci-rut {
	margin: 8px 0 0;
	font-size: 14px;
	color: var(--ap-slate);
	font-variant-numeric: tabular-nums;
}

// Botón "Volver" (mismo pill del panel; la flecha acompaña la dirección) -----
.pill {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	height: 40px;
	padding: 0 18px;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	background-color: var(--ap-paper);
	color: var(--ap-ink-2);
	font-family: var(--ap-font);
	font-size: 13.5px;
	font-weight: 600;
	cursor: pointer;
	transition: transform 0.16s var(--ap-ease), border-color 0.16s var(--ap-ease),
		color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

	&:hover {
		transform: translateY(-1px);
		border-color: var(--ap-teal);
		color: var(--ap-teal-deep);
		box-shadow: 0 6px 18px -10px rgba(14, 47, 52, 0.5);
	}

	&:hover .pill__arrow {
		transform: translateX(-2px);
	}

	&:active {
		transform: translateY(0);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

.pill__arrow {
	font-size: 16px;
	line-height: 1;
	transition: transform 0.16s var(--ap-ease);
}

// Cuerpo: rail de facetas + panel de contenido ------------------------------
.ci-body {
	display: flex;
	align-items: flex-start;
	gap: 20px;
}

.ci-rail {
	flex: 0 0 200px;
	display: flex;
	flex-direction: column;
	gap: 4px;
	position: sticky;
	top: 24px;
}

.ci-tab {
	appearance: none;
	text-align: left;
	padding: 10px 14px;
	border: 1px solid transparent;
	border-radius: var(--ap-radius-sm);
	background-color: transparent;
	color: var(--ap-slate);
	font-family: var(--ap-font);
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease);

	&:hover {
		background-color: var(--ap-mist);
		color: var(--ap-ink);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}

	// Faceta activa: acento teal sólido, la única señal fuerte del rail.
	&.is-active {
		background-color: var(--ap-teal-wash);
		color: var(--ap-teal-deep);
		font-weight: 600;
		box-shadow: inset 3px 0 0 var(--ap-teal);
	}
}

.ci-panel {
	flex: 1;
	min-width: 0;
	background-color: var(--ap-paper);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius);
	box-shadow: var(--ap-shadow-card);
	padding: 24px;
}

@media (max-width: 720px) {
	.ci-body {
		flex-direction: column;
	}

	.ci-rail {
		position: static;
		flex-direction: row;
		flex-wrap: wrap;
		flex-basis: auto;
	}
}
</style>
